<template>
  <div class="category-page">
    <el-card class="category-card" shadow="always">
      <div class="category-card-header">
        <div class="header-tabs">
          <div 
            class="tab-item" 
            :class="{ active: activeTab === 'category' }"
            @click="activeTab = 'category'"
          >
            <span>分类管理</span>
          </div>
          <div 
            class="tab-item" 
            :class="{ active: activeTab === 'attribute' }"
            @click="handleAttributeTabClick"
          >
            <span>特殊字段配置</span>
          </div>
        </div>
        <el-input v-if="activeTab === 'category'" placeholder="搜索分类" size="small" clearable class="search-input" />
      </div>
      
      <!-- 分类管理内容 -->
      <div v-show="activeTab === 'category'" class="category-frames">
        <el-card class="category-frame" shadow="never">
          <div class="category-frame-header">
            <span>一级分类</span>
            <el-button size="mini" type="primary" plain @click="openAddDialog(1, 'add')">新增</el-button>
          </div>
          <ul class="category-level-list">
            <li
              v-for="node in level1List"
              :key="node.id"
              :class="{ active: node.id === selectedLevel1 }"
            >
              <div class="category-item" @click.stop="selectLevel1(node)">
                <span>{{ node.name }}</span>
                <div class="category-item-actions">
                  <el-button type="text" size="mini" @click.stop="handleEdit(node)">编辑</el-button>
                  <el-button type="text" size="mini" @click.stop="handleDelete(node)">删除</el-button>
                </div>
              </div>
            </li>
          </ul>
        </el-card>
        <el-card class="category-frame" shadow="never">
          <div class="category-frame-header">
            <span>二级分类</span>
            <el-button size="mini" type="primary" plain @click="openAddDialog(2)">新增</el-button>
          </div>
          <ul class="category-level-list">
            <li
              v-for="node in level2List"
              :key="node.id"
              :class="{ active: node.id === selectedLevel2 }"
            >
              <div class="category-item" @click.stop="selectLevel2(node)">
                <span>{{ node.name }}</span>
                <div class="category-item-actions">
                  <el-button type="text" size="mini" @click.stop="handleEdit(node, 2)">编辑</el-button>
                  <el-button type="text" size="mini" @click.stop="handleDelete(node)">删除</el-button>
                </div>
              </div>
            </li>
          </ul>
        </el-card>
        <el-card class="category-frame" shadow="never">
          <div class="category-frame-header">
            <span>三级分类</span>
            <el-button size="mini" type="primary" plain @click="openAddDialog(3)">新增</el-button>
          </div>
          <ul class="category-level-list">
            <li v-for="node in level3List" :key="node.id">
              <div class="category-item">
                <span>{{ node.name }}</span>
                <div class="category-item-actions">
                  <el-button type="text" size="mini" @click.stop="handleEdit(node, 3)">编辑</el-button>
                  <el-button type="text" size="mini" @click.stop="handleDelete(node)">删除</el-button>
                </div>
              </div>
            </li>
          </ul>
        </el-card>
      </div>
      
      <!-- 特殊字段配置内容 -->
      <div v-show="activeTab === 'attribute'" class="attribute-content">
        <div class="attribute-layout">
          <!-- 左侧分类树 -->
          <div class="category-tree-panel">
            <div class="panel-header">
              <span>分类列表</span>
              <el-button type="text" size="small" @click="refreshCategoryTree">
                <i class="el-icon-refresh"></i>
              </el-button>
            </div>
            <div class="tree-container">
              <el-tree
                ref="categoryTreeRef"
                :data="categoryTreeData"
                :props="treeProps"
                :expand-on-click-node="false"
                :default-expand-all="true"
                :highlight-current="true"
                node-key="id"
                @node-click="handleTreeNodeClick"
              >
                <template #default="{ node, data }">
                  <div class="tree-node">
                    <span class="node-label">{{ node.label }}</span>
                    <span class="node-count">({{ data.attributeCount || 0 }})</span>
                  </div>
                </template>
              </el-tree>
            </div>
          </div>

          <!-- 右侧属性配置 -->
          <div class="attribute-panel">
            <div class="panel-header">
              <div class="header-left">
                <span v-if="selectedCategoryInfo">
                  属性配置 - {{ selectedCategoryInfo.name }}
                </span>
                <span v-else>属性配置</span>
              </div>
              <div class="header-right">
                <el-button type="primary" @click="openConfigDialog" :disabled="!selectedCategoryId">
                  新增属性配置
                </el-button>
              </div>
            </div>

            <!-- 属性配置列表 -->
            <div class="attribute-list" v-if="selectedCategoryId">
              <el-table :data="attributeList" style="width: 100%" v-loading="loading">
                <el-table-column prop="attributeName" label="属性名称" width="200" />
                <el-table-column prop="attributeType" label="属性类型" width="120">
                  <template #default="scope">
                    <el-tag :type="getAttributeTypeTag(scope.row.attributeType)">
                      {{ getAttributeTypeLabel(scope.row.attributeType) }}
                    </el-tag>
                  </template>
                </el-table-column>
                <el-table-column label="选项数量" width="100">
                  <template #default="scope">
                    <span v-if="scope.row.attributeType === 'select'" class="option-count">
                      {{ scope.row.optionCount || 0 }} 项
                    </span>
                    <span v-else class="option-count">-</span>
                  </template>
                </el-table-column>
                <el-table-column label="操作" width="148" class-name="op-col">
                  <template #default="scope">
                    <div class="op-cell">
                      <el-button type="primary" link size="small" @click="handleEditConfig(scope.row)">编辑</el-button>
                      <el-button type="danger" link size="small" @click="handleDeleteConfig(scope.row)">删除</el-button>
                    </div>
                  </template>
                </el-table-column>
              </el-table>
            </div>

            <!-- 无数据提示 -->
            <div v-else class="no-data">
              <el-empty description="请选择左侧分类查看属性配置" />
            </div>
          </div>
        </div>
      </div>
    </el-card>
    <el-dialog :title="dialogTitle" v-model="addDialogVisible" width="420px">
      <el-form :model="addForm" label-width="80px">
        <el-form-item label="名称" :rules="[{ required: true, message: '请输入名称', trigger: 'blur' }]">
          <el-input v-model="addForm.name" placeholder="请输入分类名称" />
        </el-form-item>
        <el-form-item label="图片">
          <el-upload
            class="category-upload"
            action="/api/upload/image"
            list-type="picture-card"
            :file-list="uploadList"
            :limit="1"
            accept="image/*"
            :on-success="handleUploadSuccess"
            :on-remove="handleUploadRemove"
            :on-exceed="handleUploadExceed"
          >
            <div class="upload-placeholder">
              <i class="el-icon-plus"></i>
              <div>上传图片</div>
            </div>
          </el-upload>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="addDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="confirmAdd">确定</el-button>
      </template>
    </el-dialog>

    <!-- 属性配置对话框 -->
    <el-dialog :title="configDialogTitle" v-model="configDialogVisible" width="500px">
      <el-form :model="configForm" :rules="configRules" ref="configFormRef" label-width="80px">
        <el-form-item label="属性名称" prop="attributeName">
          <el-input v-model="configForm.attributeName" placeholder="请输入属性名称" />
        </el-form-item>
        <el-form-item label="属性类型" prop="attributeType">
          <el-select v-model="configForm.attributeType" placeholder="请选择属性类型" style="width: 100%" @change="handleAttributeTypeChange">
            <el-option label="文本输入" value="text" />
            <el-option label="下拉选择" value="select" />
          </el-select>
        </el-form-item>
        
        <!-- 下拉选项管理 -->
        <div v-if="configForm.attributeType === 'select'" class="option-section">
          <el-divider content-position="left">下拉选项管理</el-divider>
          <div class="option-list">
            <div class="option-header">
              <span>选项列表</span>
              <el-button type="text" size="small" @click="addOption">
                <i class="el-icon-plus"></i> 添加选项
              </el-button>
            </div>
            <div v-if="optionList.length === 0" class="no-options">
              <el-empty description="暂无选项，请添加选项" :image-size="80" />
            </div>
            <div v-else class="option-items">
              <div v-for="(option, index) in optionList" :key="option.id || index" class="option-item">
                <el-input 
                  v-model="option.optionValue" 
                  placeholder="下拉值" 
                  size="small"
                  style="width: 200px; margin-right: 8px;"
                />
                <el-button type="text" size="small" style="color: #f56c6c" @click="removeOption(index)">
                  删除
                </el-button>
              </div>
            </div>
          </div>
        </div>
      </el-form>
      <template #footer>
        <el-button @click="configDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="confirmConfig">确定</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import axios from 'axios'
import { normalizeImgUrl } from '@/utils/mediaUrl'

// 页签状态
const activeTab = ref('category')

// 分类管理数据
const level1List = ref([])
const level2List = ref([])
const level3List = ref([])
const selectedLevel1 = ref(null)
const selectedLevel2 = ref(null)

// 特殊字段配置数据
const selectedCategoryId = ref(null)
const selectedCategoryInfo = ref(null)
const categoryTreeData = ref([])
const attributeList = ref([])
const configDialogVisible = ref(false)
const configForm = ref({
  attributeName: '',
  attributeType: 'text'
})

const optionList = ref([])
const configFormRef = ref()
const editingConfig = ref(null)
const loading = ref(false)
const categoryTreeRef = ref()

// 树形组件配置
const treeProps = {
  children: 'children',
  label: 'name'
}

// 表单验证规则
const configRules = {
  attributeName: [
    { required: true, message: '请输入属性名称', trigger: 'blur' }
  ],
  attributeType: [
    { required: true, message: '请选择属性类型', trigger: 'change' }
  ]
}

const loadLevel1 = async () => {
  try {
    const res = await axios.get('/api/categories/list', { params: { pid: 0, size: 999 } })
    level1List.value = res.data?.data?.records || []
  } catch (error) {
    ElMessage.error('加载一级分类失败')
  }
}

const findNameById = (list, id) => list.find((item) => item.id === id)?.name || ''

const buildCategoryPath = () => {
  const name = addForm.value.name?.trim() || ''
  if (addDialogLevel.value === 1) {
    return name
  }

  const parentIdLevel2 = addDialogLevel.value === 2
    ? addDialogRecord.value?.pid || selectedLevel1.value
    : addDialogRecord.value?.pid || selectedLevel2.value

  if (addDialogLevel.value === 2) {
    const parentName = findNameById(level1List.value, parentIdLevel2)
    return `${parentName}/${name}`.replace(/^\/|\/$/g, '')
  }

  const level2Node = level2List.value.find((item) => item.id === parentIdLevel2)
  const level2Name = level2Node?.name || ''
  const level1Id = level2Node?.pid || selectedLevel1.value
  const level1Name = findNameById(level1List.value, level1Id)
  return `${level1Name}/${level2Name}/${name}`.replace(/^\/|\/$/g, '')
}

const loadLevel2 = async (pid) => {
  if (!pid) {
    level2List.value = []
    return
  }
  try {
    const res = await axios.get('/api/categories/list', { params: { pid, size: 999 } })
    level2List.value = res.data?.data?.records || []
  } catch (error) {
    ElMessage.error('加载二级分类失败')
  }
}

const loadLevel3 = async (pid) => {
  if (!pid) {
    level3List.value = []
    return
  }
  try {
    const res = await axios.get('/api/categories/list', { params: { pid, size: 999 } })
    level3List.value = res.data?.data?.records || []
  } catch (error) {
    ElMessage.error('加载三级分类失败')
  }
}

const selectLevel1 = (node) => {
  selectedLevel1.value = node.id
  selectedLevel2.value = null
  loadLevel2(node.id)
  level3List.value = []
}

const selectLevel2 = (node) => {
  selectedLevel2.value = node.id
  loadLevel3(node.id)
}

const addLevel1 = async () => {
  openAddDialog(1)
}

const addLevel2 = async () => {
  openAddDialog(2)
}

const addLevel3 = async () => {
  openAddDialog(3)
}

const addDialogVisible = ref(false)
const addDialogLevel = ref(1)
const addForm = ref({ name: '', image: '' })
const uploadList = ref([])

const pickUploadUrl = (value) => {
  if (!value) return ''
  if (typeof value === 'string') return value
  if (value.url) return value.url
  if (value.data) {
    if (typeof value.data === 'string') return value.data
    if (typeof value.data === 'object' && value.data.url) return value.data.url
  }
  if (value.response) {
    return pickUploadUrl(value.response)
  }
  return ''
}

const toRelativeUrl = (url) => {
  if (!url) return ''
  const str = String(url)
  try {
    const u = new URL(str)
    return u.pathname + u.search + u.hash
  } catch {
    return str
  }
}

const toDisplayUrl = (url) => normalizeImgUrl(url)

const handleUploadSuccess = (_response, _file, fileList) => {
  const latest = fileList[fileList.length - 1]
  const rawUrl = pickUploadUrl(latest)
  const saveUrl = toRelativeUrl(rawUrl)
  addForm.value.image = saveUrl
  uploadList.value = fileList.map((item) => {
    const u = pickUploadUrl(item)
    return { ...item, url: toDisplayUrl(u) }
  })
}

const handleUploadRemove = () => {
  addForm.value.image = ''
  uploadList.value = []
}

const handleUploadExceed = () => {
  ElMessage.warning('最多只能上传一张图片')
}

const dialogTitle = computed(() => {
  const action = addDialogMode.value === 'edit' ? '编辑' : '新增'
  if (addDialogLevel.value === 1) return `${action}一级分类`
  if (addDialogLevel.value === 2) return `${action}二级分类`
  return `${action}三级分类`
})

const openAddDialog = (level, mode = 'add', record = null) => {
  addDialogLevel.value = level
  addDialogMode.value = mode
  addDialogRecord.value = record
  addForm.value = {
    name: record?.name || '',
    image: record?.image || ''
  }
  uploadList.value = record?.image ? [{ uid: 'init', url: toDisplayUrl(record.image) }] : []
  addDialogVisible.value = true
}

const addDialogMode = ref('add')
const addDialogRecord = ref(null)

const confirmAdd = async () => {
  const name = addForm.value.name?.trim()
  if (!name) {
    ElMessage.warning('请输入分类名称')
    return
  }
  let pid = 0
  if (addDialogLevel.value === 2 && selectedLevel1.value) {
    pid = selectedLevel1.value
  }
  if (addDialogLevel.value === 3 && selectedLevel2.value) {
    pid = selectedLevel2.value
  }
  if ((addDialogLevel.value === 2 && !selectedLevel1.value) ||
      (addDialogLevel.value === 3 && !selectedLevel2.value)) {
    ElMessage.warning('请选择对应父分类')
    return
  }
  try {
    const payload = {
      pid,
      name,
      image: addForm.value.image,
      level: addDialogLevel.value,
      path: buildCategoryPath()
    }
    if (addDialogMode.value === 'edit' && addDialogRecord.value) {
      payload.id = addDialogRecord.value.id
    }
    await axios.post('/api/categories/submit', payload)
    ElMessage.success(addDialogMode.value === 'edit' ? '分类已更新' : '分类已添加')
    addDialogVisible.value = false
    loadLevel1()
    if (selectedLevel1.value) loadLevel2(selectedLevel1.value)
    if (selectedLevel2.value) loadLevel3(selectedLevel2.value)
  } catch (error) {
    ElMessage.error('保存失败')
  }
}

const handleEdit = (node, level) => {
  openAddDialog(level, 'edit', node)
}

const handleDelete = (node) => {
  if (!node?.id) return
  ElMessageBox.confirm('删除后不可恢复，是否继续？', '确认删除', { type: 'warning' })
    .then(async () => {
      try {
        await axios.post('/api/categories/remove', null, { params: { ids: String(node.id) } })
        ElMessage.success('删除成功')
        if (node.id === selectedLevel1.value) {
          selectedLevel1.value = null
          level2List.value = []
          level3List.value = []
        }
        if (node.id === selectedLevel2.value) {
          selectedLevel2.value = null
          level3List.value = []
        }
        loadLevel1()
        if (selectedLevel1.value) loadLevel2(selectedLevel1.value)
        if (selectedLevel2.value) loadLevel3(selectedLevel2.value)
      } catch (error) {
        ElMessage.error('删除失败')
      }
    })
}

// 处理特殊字段配置页签点击
const handleAttributeTabClick = () => {
  // 切换到特殊字段配置页签
  activeTab.value = 'attribute'
  if (!categoryTreeData.value.length) {
    // 如果没有加载分类数据，加载分类树
    loadCategoryTree()
  }
}

// 特殊字段配置相关方法
const loadCategoryTree = async () => {
  try {
    loading.value = true
    const res = await axios.get('/api/categories/list', { params: { size: 999 } })
    const categories = res.data?.data?.records || []
    categoryTreeData.value = buildCategoryTreeData(categories)
    // 预加载所有节点的属性数量，避免默认显示0
    const collectIds = (nodes, ids) => {
      for (const node of nodes) {
        ids.push(node.id)
        if (node.children && node.children.length > 0) {
          collectIds(node.children, ids)
        }
      }
    }
    const allIds = []
    collectIds(categoryTreeData.value, allIds)
    await Promise.all(allIds.map(id => updateTreeNodeAttributeCount(id)))
  } catch (error) {
    ElMessage.error('加载分类失败')
  } finally {
    loading.value = false
  }
}

const buildCategoryTreeData = (categories) => {
  const tree = []
  const map = {}
  
  // 创建映射
  categories.forEach(item => {
    map[item.id] = { 
      ...item, 
      children: [],
      attributeCount: 0 // 初始化属性数量
    }
  })
  
  // 构建树形结构
  categories.forEach(item => {
    if (item.pid === 0) {
      tree.push(map[item.id])
    } else if (map[item.pid]) {
      map[item.pid].children.push(map[item.id])
    }
  })
  
  return tree
}

const handleTreeNodeClick = async (data) => {
  selectedCategoryId.value = data.id
  selectedCategoryInfo.value = data
  await loadAttributeConfigs(data.id)
  // 可以在这里更新树节点的属性数量
  updateTreeNodeAttributeCount(data.id)
}

const refreshCategoryTree = () => {
  loadCategoryTree()
}

const updateTreeNodeAttributeCount = async (categoryId) => {
  try {
    const res = await axios.get('/api/meta-category-attribute-config/by-category/' + categoryId)
    const count = res.data?.data?.length || 0
    
    // 更新树节点数据
    const updateNodeCount = (nodes) => {
      for (let node of nodes) {
        if (node.id === categoryId) {
          node.attributeCount = count
          break
        }
        if (node.children && node.children.length > 0) {
          updateNodeCount(node.children)
        }
      }
    }
    
    updateNodeCount(categoryTreeData.value)
  } catch (error) {
    console.error('更新属性数量失败', error)
  }
}

const loadAttributeConfigs = async (categoryId) => {
  try {
    const res = await axios.get('/api/meta-category-attribute-config/by-category/' + categoryId)
    const configs = res.data?.data || []
    
    // 为每个配置加载选项数量
    for (let config of configs) {
      if (config.attributeType === 'select') {
        try {
          const optionRes = await axios.get('/api/meta-category-attribute-option/by-config/' + config.id)
          config.optionCount = optionRes.data?.data?.length || 0
        } catch (error) {
          config.optionCount = 0
        }
      } else {
        config.optionCount = 0
      }
    }
    
    attributeList.value = configs
  } catch (error) {
    ElMessage.error('加载属性配置失败')
  }
}

const openConfigDialog = () => {
  if (!selectedCategoryId.value) {
    ElMessage.warning('请先选择分类')
    return
  }
  editingConfig.value = null
  configForm.value = {
    attributeName: '',
    attributeType: 'text',
    categoryId: selectedCategoryId.value
  }
  optionList.value = []
  configDialogVisible.value = true
}

const handleEditConfig = async (config) => {
  editingConfig.value = config
  configForm.value = { 
    ...config,
    categoryId: selectedCategoryId.value
  }
  
  // 如果是下拉类型，加载选项
  if (config.attributeType === 'select') {
    await loadOptions(config.id)
  } else {
    optionList.value = []
  }
  
  configDialogVisible.value = true
}

// 处理属性类型变化
const handleAttributeTypeChange = (value) => {
  // 如果切换到下拉类型，初始化一个空选项
  if (value === 'select' && optionList.value.length === 0) {
    optionList.value.push({
      optionValue: '',
      optionLabel: '',
      sortOrder: 0
    })
  } else if (value === 'text') {
    // 如果切换到文本类型，清空选项列表
    optionList.value = []
  }
}



const confirmConfig = async () => {
  try {
    await configFormRef.value.validate()
    
    const payload = { 
      ...configForm.value,
      categoryLevel: selectedCategoryInfo.value?.level || 1
    }
    let configId
    
    if (editingConfig.value) {
      payload.id = editingConfig.value.id
      const res = await axios.post('/api/meta-category-attribute-config/submit', payload)
      configId = editingConfig.value.id
    } else {
      const res = await axios.post('/api/meta-category-attribute-config/submit', payload)
      configId = res.data?.data?.id
    }
    
    // 如果是下拉类型，保存选项
    if (payload.attributeType === 'select' && configId) {
      try {
        await saveOptions(configId)
      } catch (optionError) {
        ElMessage.error('选项保存失败，但属性配置已保存')
        console.error('选项保存失败:', optionError)
      }
    }
    
    ElMessage.success(editingConfig.value ? '更新成功' : '添加成功')
    configDialogVisible.value = false
    loadAttributeConfigs(selectedCategoryId.value)
  } catch (error) {
    if (error.response?.data?.msg) {
      ElMessage.error(error.response.data.msg)
    } else {
      ElMessage.error('保存失败')
    }
  }
}

const handleDeleteConfig = (config) => {
  ElMessageBox.confirm('删除后不可恢复，是否继续？', '确认删除', { type: 'warning' })
    .then(async () => {
      try {
        await axios.post('/api/meta-category-attribute-config/remove', null, { 
          params: { ids: String(config.id) } 
        })
        ElMessage.success('删除成功')
        loadAttributeConfigs(selectedCategoryId.value)
      } catch (error) {
        ElMessage.error('删除失败')
      }
    })
}

// 选项管理相关方法
const loadOptions = async (configId) => {
  try {
    const res = await axios.get('/api/meta-category-attribute-option/by-config/' + configId)
    const options = res.data?.data || []
    // 确保显示名称和选项值一致
    optionList.value = options.map(option => ({
      ...option,
      optionLabel: option.optionValue
    }))
  } catch (error) {
    optionList.value = []
  }
}

const saveOptions = async (configId) => {
  try {
    // 先获取现有选项
    const res = await axios.get('/api/meta-category-attribute-option/by-config/' + configId)
    const existingOptions = res.data?.data || []
    
    // 删除原有选项
    if (existingOptions.length > 0) {
      const optionIds = existingOptions.map(option => option.id).join(',')
      await axios.post('/api/meta-category-attribute-option/remove', null, { 
        params: { ids: optionIds } 
      })
    }
    
    // 保存新选项
    for (let option of optionList.value) {
      if (option.optionValue && option.optionValue.trim()) {
        const optionPayload = {
          configId: configId,
          optionValue: option.optionValue.trim(),
          optionLabel: option.optionValue.trim(), // 显示名称和选项值相同
          sortOrder: optionList.value.indexOf(option)
        }
        await axios.post('/api/meta-category-attribute-option/submit', optionPayload)
      }
    }
  } catch (error) {
    console.error('保存选项失败', error)
    throw error // 重新抛出错误，让上层处理
  }
}

const addOption = () => {
  optionList.value.push({
    optionValue: '',
    optionLabel: '', // 显示名称和选项值相同
    sortOrder: optionList.value.length
  })
}

const removeOption = (index) => {
  optionList.value.splice(index, 1)
  // 重新排序
  optionList.value.forEach((option, i) => {
    option.sortOrder = i
  })
}

const getAttributeTypeLabel = (type) => {
  const labels = {
    text: '文本',
    select: '下拉'
  }
  return labels[type] || type
}

const getAttributeTypeTag = (type) => {
  const tags = {
    text: '',
    select: 'success'
  }
  return tags[type] || ''
}

// 计算属性
const configDialogTitle = computed(() => {
  return editingConfig.value ? '编辑属性配置' : '新增属性配置'
})

loadLevel1()
</script>

<style scoped>
.category-page {
  padding: 24px;
  padding-bottom: 72px;
  min-height: calc(100vh - 96px);
  background: #f5f7fb;
  display: flex;
  justify-content: center;
  align-items: flex-start;
}
.category-card {
  width: 100%;
  max-width: 1100px;
  padding: 24px;
  background: #ffffff;
  display: flex;
  flex-direction: column;
  height: calc(100vh - 192px);
  margin-bottom: 24px;
}
.category-card :deep(.el-card__body) {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: 0;
}
.category-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

.header-tabs {
  display: flex;
  gap: 2px;
  background: #f5f7fa;
  border-radius: 8px;
  padding: 4px;
}

.tab-item {
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-weight: 500;
  color: #606266;
  border: none;
  background: transparent;
}

.tab-item:hover {
  color: #409eff;
  background: rgba(64, 158, 255, 0.1);
}

.tab-item.active {
  background: #ffffff;
  color: #409eff;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.search-input {
  width: 240px;
}

.attribute-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 400px;
}

.attribute-layout {
  display: flex;
  gap: 16px;
  height: 100%;
  min-height: 500px;
}

.category-tree-panel {
  width: 300px;
  min-width: 300px;
  border: 1px solid #e4e7ed;
  border-radius: 8px;
  background: #ffffff;
  display: flex;
  flex-direction: column;
}

.attribute-panel {
  flex: 1;
  border: 1px solid #e4e7ed;
  border-radius: 8px;
  background: #ffffff;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.panel-header {
  padding: 12px 16px;
  border-bottom: 1px solid #e4e7ed;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #f8f9fa;
  border-radius: 8px 8px 0 0;
}

.header-left {
  font-weight: 600;
  color: #303133;
}

.header-right {
  display: flex;
  gap: 8px;
}

.tree-container {
  flex: 1;
  padding: 8px;
  overflow-y: auto;
}

.tree-node {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding-right: 8px;
}

.node-label {
  flex: 1;
  color: #303133;
}

.node-count {
  color: #909399;
  font-size: 12px;
  background: #f0f2f5;
  padding: 2px 6px;
  border-radius: 10px;
  min-width: 20px;
  text-align: center;
}

.attribute-list {
  flex: 1;
  padding: 16px;
  overflow-y: auto;
}

.no-data {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 300px;
}

.option-section {
  margin-top: 20px;
}

.option-list {
  border: 1px solid #e4e7ed;
  border-radius: 6px;
  padding: 12px;
  background: #fafafa;
}

.option-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  font-weight: 600;
  color: #303133;
}

.no-options {
  text-align: center;
  padding: 20px 0;
}

.option-items {
  max-height: 200px;
  overflow-y: auto;
}

.option-item {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
  padding: 8px;
  background: #ffffff;
  border: 1px solid #e4e7ed;
  border-radius: 4px;
}

.option-item:last-child {
  margin-bottom: 0;
}

.option-count {
  color: #909399;
  font-size: 14px;
}
.category-boxes {
}
.category-frames {
  display: flex;
  justify-content: space-between;
  align-items: stretch;
  gap: 16px;
  flex: 1 1 auto;
  min-height: 0;
  height: 100%;
}
.category-frame {
  border-radius: 12px;
  padding: 20px;
  background: #ffffff;
  border: 1px solid rgba(30, 64, 175, 0.12);
  box-shadow: 0 0 0 rgba(0, 0, 0, 0);
  flex: 1;
  max-width: 33%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-height: 0;
}
.category-frame-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}
.category-box-title {
  font-weight: 600;
  margin-bottom: 8px;
  color: #1f2937;
}
.category-box-content {
  color: #4b5563;
}
.category-frame-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}
.category-level-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding-right: 4px;
  height: 100%;
}
.category-level-list li {
  padding: 10px 12px;
  border-radius: 8px;
  background: #fff;
  cursor: pointer;
  transition: background 0.2s ease;
  flex-shrink: 0;
}
.category-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}
.category-item span {
  flex: 1;
}
.category-item-actions {
  display: flex;
  gap: 8px;
}
.category-level-list li.active {
  background: #eef2ff;
  color: #4338ca;
}
.category-level-list li:hover {
  background: #f0f2f7;
}
@media (max-width: 960px) {
  .category-frames {
    flex-direction: column;
  }
  .category-frame {
    max-width: 100%;
    min-height: 200px;
  }
  
  .attribute-layout {
    flex-direction: column;
    gap: 12px;
  }
  
  .category-tree-panel {
    width: 100%;
    min-width: auto;
    max-height: 300px;
  }
}

@media (max-width: 768px) {
  .category-card {
    padding: 18px;
  }
  .search-input {
    width: 180px;
  }
  
  .attribute-layout {
    gap: 8px;
    min-height: 400px;
  }
  
  .category-tree-panel {
    max-height: 250px;
  }
  
  .panel-header {
    padding: 8px 12px;
  }
  
  .attribute-list {
    padding: 12px;
  }
}
</style>
